'use client';

/**
 * Hero smart input — the shell (Wave H1).
 *
 * One component renders the primary CTA on six marketing surfaces (home
 * hero/closer, /for-teams hero/closer, /for-dev-teams hero/closer). Since
 * Wave H1 it is a thin shell over two arms:
 *
 *   - HeroMessageFirst — "Message Tessa" (sms:/wa.me, desktop QR panel);
 *   - HeroCallForm     — "type your number, Tessa calls YOU" (pre-H1 form).
 *
 * Which arm a visitor sees is resolved CLIENT-SIDE after hydration so the
 * pages stay static: the static HTML carries `ssrHeroCtaVariant` (message
 * when NEXT_PUBLIC_ONBOARDING_CTA_MESSAGE_PCT > 0, today's call form when
 * unset/0 = byte-identical rollback), then the effect reads the `apr_aid`
 * cookie (minted in middleware.ts; client fallback for cached HTML), the
 * `?cta=message|call` QA override, and the UA, and re-renders if the bucket
 * says otherwise — inside a same-height row, so nothing shifts.
 *
 * Instrumentation (Wave H0): `hero_cta_view` once per surface when the CTA
 * is ≥50% visible; `hero_cta_click` on every action. Both anonymous — the
 * payload is the variant, its source, device, surface, the anon id and the
 * attribution ref that the prefilled body (`Hola Tessa #REF`) and the call
 * POST carry to `onboarding_calls.campaign`.
 *
 * Renders NOTHING when neither the call flag nor a message number is set.
 * NEXT_PUBLIC_ONBOARDING_SMS_NUMBER / _WHATSAPP_NUMBER (E.164) are each
 * opt-in and independently killable; both are answered by the same
 * SMS_ONBOARDING_ENABLED rails (channel-agnostic composer).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import {
  buildSmsHref,
  buildWhatsAppHref,
  detectMobile,
  detectMobilePlatform,
  heroBody,
  mintHeroRef,
  normalizeHeroRef,
  parseCtaOverride,
  resolveHeroCtaVariant,
  ssrHeroCtaVariant,
  type HeroCtaChannel,
  type HeroCtaDevice,
  type HeroCtaSurface,
  type HeroCtaVariant,
  type HeroCtaVariantSource,
  type MobilePlatform,
} from '@/lib/onboarding/hero-cta';
import { getOrMintAnonIdInBrowser } from '@/lib/onboarding/anon-id';
import { HERO_CTA_COPY } from '@/lib/onboarding/hero-cta-copy';
import { DEFAULT_SITE_LOCALE } from '@/lib/marketing/locale-routing';
import type { SiteLocale } from '@/lib/marketing/page-metadata';
import {
  HERO_CTA_CALL_ENABLED as CALL_ENABLED,
  HERO_CTA_ENABLED,
  HERO_CTA_HAS_MESSAGE_NUMBER as HAS_MESSAGE_NUMBER,
  HERO_CTA_MESSAGE_PCT as MESSAGE_PCT,
  HERO_CTA_SMS_NUMBER as SMS_NUMBER,
  HERO_CTA_WA_NUMBER as WA_NUMBER,
} from '@/lib/onboarding/hero-cta-env';
import {
  trackHeroCtaClick,
  trackHeroCtaView,
  type HeroCtaEventContext,
} from '@/lib/utils/track-hero-cta';
import { HeroCallForm } from './HeroCallForm';
import { HeroMessageFirst } from './HeroMessageFirst';

// The call arm's pure predicates keep their historical import path (tests +
// any caller import them from here).
export {
  detectMode,
  isCallSubmitDisabled,
  shouldShowCaptcha,
  type PhaseName,
} from './HeroCallForm';

// The env knobs live in lib/onboarding/hero-cta-env.ts (a plain module, so
// the Server-Component pages can read the same gate) — compile-time
// NEXT_PUBLIC values, identical on server and client, so the SSR arm and
// the first client render always agree.

interface Resolved {
  variant: HeroCtaVariant;
  source: HeroCtaVariantSource;
  device: HeroCtaDevice;
  platform: MobilePlatform;
  aid: string | null;
  ref: string;
}

export function HeroSmartInput({
  className,
  surface,
  locale = DEFAULT_SITE_LOCALE,
}: {
  className?: string;
  /** Which of the six mounts this is — required, it keys the readout. */
  surface: HeroCtaSurface;
  /** The page's language: the copy, the prefilled greeting and the call's
   *  opening language all follow it. */
  locale?: SiteLocale;
}) {
  const copy = HERO_CTA_COPY[locale];
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewedRef = useRef(false);

  // Resolve the variant once the browser is known. Everything here is a
  // client-only input (cookie, query string, UA) — reading it during render
  // would desync SSR from hydration, so the static arm renders first.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const override = parseCtaOverride(params.get('cta'));
    // A ref carried by the link (the email doorway's call door:
    // `/?cta=call&ref=C…`) is reported instead of a minted one, so the
    // hero events and the call row join back to the hand-off.
    const urlRef = normalizeHeroRef(params.get('ref'));
    const aid = getOrMintAnonIdInBrowser();
    const { variant, source, carriedRef } = resolveHeroCtaVariant({
      aid,
      pct: MESSAGE_PCT,
      override,
      urlRef,
      hasMessageNumber: HAS_MESSAGE_NUMBER,
      callEnabled: CALL_ENABLED,
    });
    const ua = navigator.userAgent;
    const touch = navigator.maxTouchPoints ?? 0;
    setResolved({
      variant,
      source,
      device: detectMobile(ua, touch) ? 'mobile' : 'desktop',
      platform: detectMobilePlatform(ua, touch),
      aid,
      ref: carriedRef ?? mintHeroRef(variant),
    });
  }, []);

  const ctx: HeroCtaEventContext | null = useMemo(
    () =>
      resolved
        ? {
            variant: resolved.variant,
            variant_source: resolved.source,
            device: resolved.device,
            surface,
            locale,
            aid: resolved.aid,
            ref: resolved.ref,
          }
        : null,
    [resolved, surface, locale],
  );

  // hero_cta_view: once per mount, at ≥50% visibility (a closer far below
  // the fold counts only when the visitor actually reaches it).
  useEffect(() => {
    if (!ctx || viewedRef.current) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (viewedRef.current) return;
        if (entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) {
          viewedRef.current = true;
          trackHeroCtaView(ctx);
          io.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ctx]);

  if (!HERO_CTA_ENABLED) return null;

  // Before resolution: the static arm, device-agnostic (the mobile layout of
  // the message arm is valid everywhere; a desktop visitor's first paint
  // swaps the primary into the QR toggle right after hydration).
  const variant: HeroCtaVariant =
    resolved?.variant ??
    ssrHeroCtaVariant({
      pct: MESSAGE_PCT,
      hasMessageNumber: HAS_MESSAGE_NUMBER,
      callEnabled: CALL_ENABLED,
    });
  const device: HeroCtaDevice = resolved?.device ?? 'mobile';
  const platform: MobilePlatform = resolved?.platform ?? 'other';
  const body = heroBody(resolved?.ref ?? null, locale);

  const smsHref = SMS_NUMBER ? buildSmsHref(SMS_NUMBER, body, platform) : '';
  const waHref = WA_NUMBER ? buildWhatsAppHref(WA_NUMBER, body) : '';
  // The QR is scanned by an unknown phone → the cross-platform separator.
  const qrUri = SMS_NUMBER ? buildSmsHref(SMS_NUMBER, body, 'other') : '';
  const smsDisplay = SMS_NUMBER ? formatPhoneNumberIntl(SMS_NUMBER) || SMS_NUMBER : '';

  const onAction = (channel: HeroCtaChannel) => {
    if (ctx) trackHeroCtaClick(ctx, channel);
  };

  if (variant === 'call') {
    return (
      <HeroCallForm
        className={className}
        rootRef={rootRef}
        smsHref={smsHref}
        waHref={waHref}
        smsDisplay={smsDisplay}
        heroRef={resolved?.ref ?? null}
        onAction={onAction}
        copy={copy}
        locale={locale}
      />
    );
  }

  return (
    <HeroMessageFirst
      className={className}
      rootRef={rootRef}
      device={device}
      smsNumber={SMS_NUMBER}
      waNumber={WA_NUMBER}
      smsHref={smsHref}
      waHref={waHref}
      qrUri={qrUri}
      smsDisplay={smsDisplay}
      onAction={onAction}
      copy={copy}
    />
  );
}
